import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { supabase } from '../../supabase';
import { randomUuid } from '../../utils/randomUuid';
import { writeAuditEvent, AUDIT_ENTITY_TYPES } from '../../services/auditEvents';

function recomputeProgress(rows, prior) {
  if (!rows?.length) {
    return prior || { done: 0, total: 0 };
  }
  const done = rows.filter((r) => r.status === 'done').length;
  const total = rows.length;
  const noService = rows.filter((r) => r.status === 'no_service').length;
  return { done, total, noService };
}

function todayLocalStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Row actions for customer_list + live list row updates after child cards complete.
 */
export default function useAiCustomerListActions({
  role,
  messagesRef,
  setMessages,
  sendMessage,
  beforeQuickLogCompose,
}) {
  const intl = useIntl();
  // Synchronous lock guard. messagesRef updates via useEffect AFTER React
  // commits, so two rapid taps on different rows can both pass the
  // `_rowLockedBy` check before the first commit lands. This ref flips
  // immediately on the first tap so the second tap is rejected.
  const lockedListsRef = useRef(new Set());

  const mutateListRows = useCallback((listMessageId, customerId, patch) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== listMessageId || m.action?.type !== 'customer_list') return m;
        const rows = (m.action.rows || []).map((r) =>
          r.customer_id === customerId ? { ...r, ...patch } : r,
        );
        const progress = recomputeProgress(rows, m.action.progress);
        return {
          ...m,
          action: { ...m.action, rows, progress },
        };
      }),
    );
  }, [setMessages]);

  const onChildActionCompleted = useCallback(
    (childMessageId) => {
      const msg = messagesRef.current?.find((m) => m.id === childMessageId);
      const flip = msg?._flipListRow;
      if (!flip?.listMessageId || !flip.customerId) return;
      if (flip.kind === 'log_done' || flip.kind === 'summary_done' || flip.kind === 'followup_done') {
        mutateListRows(flip.listMessageId, flip.customerId, { status: 'done' });
      }
      // Release the per-list row lock so the worker can move on to the next
      // customer. Lock is strict during the action (no mid-flight pivot) but
      // opens as soon as the child resolves (completed or failed).
      lockedListsRef.current.delete(flip.listMessageId);
      setMessages((prev) => prev.map((m) =>
        m.id === flip.listMessageId && m._rowLockedBy === flip.customerId
          ? { ...m, _rowLockedBy: null }
          : m,
      ));
    },
    [messagesRef, mutateListRows, setMessages],
  );

  // D1: release row lock when a child card is dismissed (without marking row done)
  const onChildActionDismissed = useCallback(
    (childMessageId) => {
      const msg = messagesRef.current?.find((m) => m.id === childMessageId);
      const flip = msg?._flipListRow;
      if (!flip?.listMessageId) return;
      lockedListsRef.current.delete(flip.listMessageId);
      setMessages((prev) => prev.map((m) =>
        m.id === flip.listMessageId && m._rowLockedBy === flip.customerId
          ? { ...m, _rowLockedBy: null }
          : m,
      ));
    },
    [messagesRef, setMessages],
  );

  const verifyCustomerInList = useCallback((listMessageId, customerId) => {
    const list = messagesRef.current?.find((m) => m.id === listMessageId);
    const rows = list?.action?.rows;
    if (!rows?.length) return null;
    return rows.find((r) => r.customer_id === customerId) || null;
  }, [messagesRef]);

  const handleRowAction = useCallback(
    (listMessageId, customerId, actionType, rowPayload = {}) => {
      if (role !== 'worker' && role !== 'supervisor') return;

      const row = verifyCustomerInList(listMessageId, customerId);
      if (!row) return;

      // Step-by-step lock: once a row is tapped, freeze the list so the worker
      // can't pivot to a different customer mid-action. The list stays visible
      // (rows are still readable as context) but further taps are ignored.
      // We check BOTH the persisted state flag AND a synchronous ref to close
      // the race window between React's commit and the messagesRef useEffect.
      const listMsg = messagesRef.current?.find((m) => m.id === listMessageId);
      if (listMsg?._rowLockedBy || lockedListsRef.current.has(listMessageId)) return;
      lockedListsRef.current.add(listMessageId);
      setMessages((prev) => prev.map((m) =>
        m.id === listMessageId ? { ...m, _rowLockedBy: customerId } : m,
      ));

      const name = row.name || rowPayload.customer_name || '';
      const patterns =
        row.action_payload?.service_patterns ||
        rowPayload.service_patterns ||
        [];

      if (actionType === 'quick_log_compose') {
        beforeQuickLogCompose?.();
        setMessages((prev) => {
          const marked = prev.map((m) =>
            m.action?.type === 'quick_log_compose' && m.actionStatus == null
              ? { ...m, actionStatus: 'stale' }
              : m,
          );
          return [
            ...marked,
            {
              id: randomUuid(),
              role: 'assistant',
              text: '',
              action: {
                type: 'quick_log_compose',
                customer_id: customerId,
                customer_name: name,
                service_patterns: patterns,
              },
              actionStatus: null,
              createdAt: Date.now(),
              _sourceListMessageId: listMessageId,
            },
          ];
        });
        return;
      }

      if (actionType === 'send_job_summary') {
        const jobId = row.action_payload?.job_id || rowPayload.job_id;
        if (!jobId) return;
        setMessages((prev) => [
          ...prev,
          {
            id: randomUuid(),
            role: 'assistant',
            text: '',
            action: {
              type: 'send_job_summary',
              job_id: jobId,
              // Pre-seed the summary card with customer identity so the
              // worker always sees WHO the summary is for — even if the
              // card's downstream Supabase fetch is slow or blocked by RLS.
              preview: {
                customer_id: customerId,
                customer_name: name,
                service_date: todayLocalStr(),
              },
            },
            actionStatus: 'pending',
            actionResult: null,
            createdAt: Date.now(),
            _flipListRow: { listMessageId, customerId, kind: 'summary_done' },
          },
        ]);
        return;
      }

      if (actionType === 'resolve_follow_up') {
        const note =
          typeof row.action_payload?.follow_up_note === 'string'
            ? row.action_payload.follow_up_note
            : '';
        setMessages((prev) => [
          ...prev,
          {
            id: randomUuid(),
            role: 'assistant',
            text: '',
            action: {
              type: 'resolve_follow_up',
              customer_id: customerId,
              follow_up_note: note || ' ',
            },
            actionStatus: 'pending',
            actionResult: null,
            createdAt: Date.now(),
            _flipListRow: { listMessageId, customerId, kind: 'followup_done' },
          },
        ]);
        return;
      }

      if (actionType === 'recall') {
        sendMessage(
          intl.formatMessage(
            { id: 'chip.recall', defaultMessage: 'What did I do at {name}?' },
            { name: name || '' },
          ),
        );
        return;
      }

      if (actionType === 'skip' || actionType === 'no_service') {
        // Append an assistant text message immediately so the worker has feedback,
        // then fire the insert. On success, flip the list row to no_service and
        // update the message text. On failure, surface an error.
        const pendingId = randomUuid();
        setMessages((prev) => [
          ...prev,
          {
            id: pendingId,
            role: 'assistant',
            text: intl.formatMessage(
              { id: 'aiAssistant.list.skipPending', defaultMessage: 'Skipping {name}…' },
              { name: name || '' },
            ),
            createdAt: Date.now(),
          },
        ]);

        (async () => {
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');
            const { data: membership } = await supabase
              .from('tenant_memberships')
              .select('tenant_id')
              .eq('user_id', user.id)
              .eq('is_active', true)
              .order('created_at', { ascending: true })
              .limit(1)
              .maybeSingle();
            const tenantId = membership?.tenant_id;
            if (!tenantId) throw new Error('No tenant');
            const today = todayLocalStr();
            const reason = rowPayload?.reason || 'Other';
            const { data: nsRow, error } = await supabase.from('no_service').insert({
              customer_id: customerId,
              worker_id: user.id,
              date: today,
              reason,
              tenant_id: tenantId,
            }).select('id').single();
            if (error) throw error;
            void writeAuditEvent({
              tenantId,
              entityType: AUDIT_ENTITY_TYPES.NO_SERVICE,
              entityId: nsRow?.id,
              eventType: 'created',
              payload: { source: 'ai_assistant' },
            });

            mutateListRows(listMessageId, customerId, { status: 'no_service' });
            setMessages((prev) =>
              prev.map((m) =>
                m.id === pendingId
                  ? {
                      ...m,
                      text: intl.formatMessage(
                        { id: 'aiAssistant.list.skipSuccess', defaultMessage: '{name} marked as no-service for today.' },
                        { name: name || '' },
                      ),
                    }
                  : m,
              ),
            );
          } catch (err) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === pendingId
                  ? {
                      ...m,
                      text: intl.formatMessage(
                        { id: 'aiAssistant.list.skipError', defaultMessage: "Couldn't mark {name} as no-service." },
                        { name: name || '' },
                      ),
                    }
                  : m,
              ),
            );
          }
        })();
      }
    },
    [role, messagesRef, verifyCustomerInList, setMessages, sendMessage, mutateListRows, intl, beforeQuickLogCompose],
  );

  const onNextStopLog = useCallback(
    (customerId) => {
      if (role !== 'worker' || !customerId) return;
      const listMsg = [...(messagesRef.current || [])]
        .reverse()
        .find(
          (m) =>
            m?.action?.type === 'customer_list' &&
            (m.action.rows || []).some((r) => r.customer_id === customerId),
        );
      if (!listMsg) return;
      handleRowAction(listMsg.id, customerId, 'quick_log_compose', {});
    },
    [role, messagesRef, handleRowAction],
  );

  /** Lock or clear the per-route row lock (used when quick log is cancelled / undone). */
  const setListRowLock = useCallback((listMessageId, customerId) => {
    if (!listMessageId) return;
    if (customerId) {
      lockedListsRef.current.add(listMessageId);
      setMessages((prev) => prev.map((m) =>
        m.id === listMessageId ? { ...m, _rowLockedBy: customerId } : m,
      ));
    } else {
      lockedListsRef.current.delete(listMessageId);
      setMessages((prev) => prev.map((m) =>
        m.id === listMessageId ? { ...m, _rowLockedBy: null } : m,
      ));
    }
  }, [setMessages]);

  return {
    handleRowAction,
    onChildActionCompleted,
    onChildActionDismissed,
    mutateListRows,
    onNextStopLog,
    setListRowLock,
  };
}
